import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { AuthContext } from '../App';
import { 
  LogOut, BookOpen, Trophy, MessageSquare, PenTool, Volume2, Brain,
  Flame, Star, Zap, Target, Award, TrendingUp, Sparkles, Crown,
  ChevronRight, PlayCircle, CheckCircle2, User, Globe, Gift, Medal,
  GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';
import ProfileCompletion from '../components/ProfileCompletion';
import LanguageSelector from '../components/LanguageSelector';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Şu an sadece İngilizce aktif - diğer diller sonra eklenecek
const LANGUAGES = [
  { code: 'english', name: 'English', native: 'English', flag: '🇬🇧' }
];

const LEVEL_COLORS = {
  'A1': 'from-green-400 to-green-500',
  'A2': 'from-blue-400 to-blue-500',
  'B1': 'from-yellow-400 to-yellow-500',
  'B2': 'from-orange-400 to-orange-500',
  'C1': 'from-red-400 to-red-500',
  'C2': 'from-purple-400 to-purple-500'
};

const DashboardDuolingo = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);

  // Demo kullanıcıları bu sayfaya erişemez
  useEffect(() => {
    if (user?.is_demo) {
      toast.error('Bu özellik tam sürümde mevcuttur');
      navigate('/dashboard');
    }
  }, [user, navigate]);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [languageStats, setLanguageStats] = useState(null);
  const [allLanguageStats, setAllLanguageStats] = useState(null);

  useEffect(() => {
    if (user) {
      if (user.target_language) {
        setSelectedLanguage(user.target_language);
      }
      checkProfileAndLanguage();
    }
  }, [user]);

  useEffect(() => {
    if (selectedLanguage) {
      loadLanguageStats();
    }
  }, [selectedLanguage]);

  const checkProfileAndLanguage = () => {
    if (user && user.email) {
      const isAdmin = user.email.endsWith('@admin') || user.email === 'admin@kariyer.com' || user.role === 'admin';
      if (!isAdmin && user.profile_completed === false) {
        setShowProfileCompletion(true);
      } else if (user.profile_completed === true && !user.target_language) {
        setShowLanguageSelector(true);
      }
    }
    setLoading(false);
  };

  const loadLanguageStats = async () => {
    try {
      const [statsRes, allStatsRes] = await Promise.all([
        axios.get(`${API}/user/stats/${selectedLanguage}`, { withCredentials: true }),
        axios.get(`${API}/user/all-language-stats`, { withCredentials: true })
      ]);
      setLanguageStats(statsRes.data);
      setAllLanguageStats(allStatsRes.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleLanguageSelected = async (language) => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      setSelectedLanguage(language);
      setShowLanguageSelector(false);
      toast.success(`${LANGUAGES.find(l => l.code === language)?.flag} Dil seçildi!`);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const changeLanguage = async (langCode) => {
    try {
      await axios.post(`${API}/auth/select-language`, { language: langCode }, { withCredentials: true });
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      setSelectedLanguage(langCode);
      const lang = LANGUAGES.find(l => l.code === langCode);
      toast.success(`${lang?.flag} ${lang?.name} seçildi!`);
    } catch (error) {
      toast.error('Dil değiştirilemedi');
    }
  };

  const handleProfileComplete = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      setShowProfileCompletion(false);
      if (!response.data.target_language) {
        setShowLanguageSelector(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('session_token');
      setUser(null);
      navigate('/');
    } catch (error) {
      toast.error('Çıkış yapılamadı');
    }
  };

  const currentLang = LANGUAGES.find(l => l.code === selectedLanguage);

  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 border-8 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-xl font-bold text-gray-700">Yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-6 bg-gradient-to-br from-emerald-50 to-green-50">
        {showProfileCompletion && <ProfileCompletion onComplete={handleProfileComplete} />}
        {showLanguageSelector && !showProfileCompletion && (
          <LanguageSelector onLanguageSelected={handleLanguageSelected} onClose={() => setShowLanguageSelector(false)} />
        )}
      
        <div className="container mx-auto px-4 max-w-4xl">
        {/* Header - Clean & Simple */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-1">
              🇬🇧 English Öğreniyorum
            </h1>
            <p className="text-sm text-gray-500">
              Merhaba {user?.name?.split(' ')[0] || 'Öğrenci'}! 
              {languageStats?.current_level && languageStats.current_level !== 'Belirlenmedi' 
                ? ` Seviyen: ${languageStats.current_level}` 
                : ' Hadi başlayalım!'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate('/profile')} variant="ghost" size="sm" className="text-gray-500">
              <User className="w-4 h-4" />
            </Button>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-gray-500 hover:text-red-500">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats Row - Soft Design */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-gray-800">{languageStats?.current_level || '-'}</p>
            <p className="text-xs text-gray-400">Seviye</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-gray-800">{languageStats?.total_xp || 0}</p>
            <p className="text-xs text-gray-400"> Mil</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-gray-800">{languageStats?.exams?.best_score?.toFixed(0) || 0}%</p>
            <p className="text-xs text-gray-400">En İyi</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-gray-800">{languageStats?.exercises?.total || 0}</p>
            <p className="text-xs text-gray-400">Egzersiz</p>
          </div>
        </div>

        {/* Öğrenme Yolu - Hero Card */}
        <Card className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer border-0 mb-4"
              onClick={() => navigate('/learning-path')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">🎯</span>
                  <Badge className="bg-white/20 text-white text-xs px-2 py-1 rounded-lg backdrop-blur">
                    A1 → C2
                  </Badge>
                </div>
                <h3 className="text-lg font-bold text-white">Öğrenme Yolu</h3>
                <p className="text-xs text-white/80">Adım adım İngilizce öğren</p>
              </div>
              <ChevronRight className="w-6 h-6 text-white" />
            </div>
          </CardContent>
        </Card>

        {/* Anlık İstatistikler - Üstte Görünsün */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-0 rounded-xl">
            <CardContent className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-600 font-medium">Çevrimiçi</span>
              </div>
              <p className="text-xl font-bold text-green-700">{Math.floor(Math.random() * 50) + 127}</p>
              <p className="text-xs text-green-600">Aktif Öğrenci</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-0 rounded-xl">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-blue-700">2,847</p>
              <p className="text-xs text-blue-600">Toplam Üye</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-0 rounded-xl">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-purple-700">156K</p>
              <p className="text-xs text-purple-600">Çözülen Soru</p>
            </CardContent>
          </Card>
        </div>

        {/* Liderlik Tablosu - Kompakt */}
        <Card className="bg-white rounded-2xl shadow-sm border-0 mb-4">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                <CardTitle className="text-base">Haftalık Liderler</CardTitle>
              </div>
              <Badge className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5">
                🏆 1. = 500 Mil
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            <div className="space-y-1.5">
              {[
                { rank: 1, name: 'Elif Y.', xp: 2840, badge: '🥇' },
                { rank: 2, name: 'Ahmet K.', xp: 2650, badge: '🥈' },
                { rank: 3, name: 'Zeynep D.', xp: 2420, badge: '🥉' },
                { rank: 4, name: 'Mehmet Ö.', xp: 2180, badge: '4' },
                { rank: 5, name: 'Ayşe Ç.', xp: 1950, badge: '5' },
              ].map((player, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center gap-2 p-2 rounded-lg ${
                    player.rank <= 3 ? 'bg-yellow-50' : 'bg-gray-50'
                  }`}
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    player.rank === 1 ? 'bg-yellow-400 text-white' :
                    player.rank === 2 ? 'bg-gray-400 text-white' :
                    player.rank === 3 ? 'bg-orange-400 text-white' :
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {player.rank <= 3 ? player.badge : player.rank}
                  </span>
                  <span className="flex-1 text-sm font-medium text-gray-700">{player.name}</span>
                  <span className="text-sm font-bold text-purple-600">{player.xp.toLocaleString()}</span>
                </div>
              ))}
              
              {/* Sen */}
              <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-100 border border-blue-300 mt-2">
                <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                  {Math.floor(Math.random() * 15) + 18}
                </span>
                <span className="flex-1 text-sm font-medium text-blue-800">{user?.name?.split(' ')[0] || 'Sen'} <span className="text-xs text-blue-600">(Sen)</span></span>
                <span className="text-sm font-bold text-blue-700">{languageStats?.xp?.total || 450}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content - Soft & Clean Design */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* Gramer Eğitimi */}
          <Card className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer border-0"
                onClick={() => navigate('/exercises')}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <Badge className="bg-blue-500 text-white text-xs px-2 py-1 rounded-lg">
                  Öğren
                </Badge>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">Gramer Eğitimi</h3>
              <p className="text-sm text-gray-500">Öğren, Pratik Yap</p>
            </CardContent>
          </Card>

          {/* Kelime Egzersizleri */}
          <Card className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer border-0"
                onClick={() => navigate('/exercises')}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <Badge className="bg-purple-500 text-white text-xs px-2 py-1 rounded-lg">
                  {languageStats?.exercises?.total || 0} Kelime
                </Badge>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">Kelime Egzersizleri</h3>
              <p className="text-sm text-gray-500">Vocabulary, Kelime Hazinesi</p>
            </CardContent>
          </Card>

          {/* Sınavlarım */}
          <Card className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer border-0"
                onClick={() => navigate('/cabin-crew/my-exams')}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <Badge className="bg-amber-500 text-white text-xs px-2 py-1 rounded-lg">
                  {languageStats?.exams?.count || 0} Sınav
                </Badge>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">Sınavlarım</h3>
              <p className="text-sm text-gray-500">Geçmiş sınav sonuçları</p>
            </CardContent>
          </Card>

          {/* İlerleme */}
          <Card className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer border-0"
                onClick={() => navigate('/cabin-crew/progress')}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <Badge className="bg-emerald-500 text-white text-xs px-2 py-1 rounded-lg">
                  {languageStats?.streak?.current || 0} Gün
                </Badge>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">İlerleme</h3>
              <p className="text-sm text-gray-500">Seri, Rozetler, İstatistikler</p>
            </CardContent>
          </Card>
        </div>

        {/* Kabin Memurluğu - Öne Çıkan */}
        <Card className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer border-0 mb-4"
              onClick={() => navigate('/cabin-crew')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-sky-400 to-indigo-600 rounded-xl flex items-center justify-center">
                <span className="text-xl">✈️</span>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-800">Kabin Memurluğu Eğitimi</h3>
                <p className="text-xs text-gray-500">ICAO, Mülakat, Havacılık İngilizcesi</p>
              </div>
              <Badge className="bg-sky-500 text-white text-xs px-2 py-0.5 rounded-lg">Pro</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Beceri Testleri - Speaking, Writing, Listening */}
        <div className="mb-4">
          <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" />
            Beceri Testleri
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {/* Speaking */}
            <Card className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer border-0"
                  onClick={() => navigate('/speaking-practice')}>
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-pink-600 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <h4 className="text-sm font-bold text-gray-800">Speaking</h4>
                <p className="text-xs text-gray-500">Konuşma</p>
              </CardContent>
            </Card>

            {/* Writing */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer border-0"
                  onClick={() => navigate('/writing-test')}>
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <PenTool className="w-5 h-5 text-white" />
                </div>
                <h4 className="text-sm font-bold text-gray-800">Writing</h4>
                <p className="text-xs text-gray-500">Yazma</p>
              </CardContent>
            </Card>

            {/* Listening */}
            <Card className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer border-0"
                  onClick={() => navigate('/listening-test')}>
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Volume2 className="w-5 h-5 text-white" />
                </div>
                <h4 className="text-sm font-bold text-gray-800">Listening</h4>
                <p className="text-xs text-gray-500">Dinleme</p>
              </CardContent>
            </Card>
          </div>
        </div>
        </div>
      </div>
    </CabinCrewLayout>
  );
};

export default DashboardDuolingo;
